import { useEffect, useMemo, useState } from 'react';
import apiClient from '@/lib/api/client';
import { getJobs, type Job } from '@/lib/api/jobs';
import { InvoiceModal } from '@/components/invoices';
import { createInvoice, type CreateInvoiceData, type UpdateInvoiceData } from '@/lib/api/invoices';

// ── Billing tally (CR #136) ─────────────────────────────────────────────────
// Erik bills a CREW RATE per wall-clock hour, not man-hours: two people on site
// 8-4 is 8 billable hours, not 16. This panel takes a date range and shows, per
// day, how many hours each HEADCOUNT was actually on site — so short-handed
// stretches can be billed at the lower rate instead of the full crew rate —
// then prices the range with an editable rate per headcount.

interface Day {
  date: string;
  crew: string[];
  headcount_max: number;
  by_headcount: Record<string, number>;
  billable_hours: number;
  man_hours: number;
  solo: { worker: string; hours: number }[];
  untimed: { worker: string; hours: number }[];
  jobs: string[];
}
interface Tally {
  from: string; to: string; days: Day[];
  totals: { by_headcount: Record<string, number>; billable_hours: number; man_hours: number };
}

const LS_RATES = 'billing_rates_by_headcount';
const DEFAULT_RATES: Record<string, number> = { '1': 55, '2': 95, '3': 120, '4': 150 };

const money = (n: number) => `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (d: string) =>
  new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export const BillingTally = () => {
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [jobId, setJobId] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [data, setData] = useState<Tally | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  // CR #174 — turn the tally straight into an invoice
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState('');
  const selectedJob = jobs.find(j => String(j.id) === jobId);
  const saveInvoice = async (payload: CreateInvoiceData | UpdateInvoiceData) => {
    const created = await createInvoice(payload as CreateInvoiceData);
    setInvoiceOpen(false);
    setInvoiceMsg(`Invoice ${created.invoiceNumber} created as a draft — find it on the job's Invoices tab.`);
    setTimeout(() => setInvoiceMsg(''), 8000);
  };
  const [rates, setRates] = useState<Record<string, number>>(() => {
    try { return { ...DEFAULT_RATES, ...JSON.parse(localStorage.getItem(LS_RATES) || '{}') }; }
    catch { return DEFAULT_RATES; }
  });

  useEffect(() => { getJobs({ limit: 200 }).then(r => setJobs(r.jobs)).catch(() => {}); }, []);

  const run = async () => {
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams({ from, to, ...(jobId ? { job_id: jobId } : {}) });
      const r = await apiClient.get(`/portal/time-entries/billing-tally?${q}`);
      setData(r.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || 'Could not build the tally.');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open && !data) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const setRate = (n: string, v: string) => {
    const next = { ...rates, [n]: Number(v) || 0 };
    setRates(next);
    localStorage.setItem(LS_RATES, JSON.stringify(next));
  };

  const priced = useMemo(() => {
    if (!data) return { lines: [] as { n: string; hours: number; rate: number; total: number }[], total: 0 };
    const lines = Object.entries(data.totals.by_headcount)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([n, hours]) => ({ n, hours, rate: rates[n] ?? 0, total: hours * (rates[n] ?? 0) }));
    return { lines, total: lines.reduce((s, l) => s + l.total, 0) };
  }, [data, rates]);

  // The invoice form opens with one line per headcount tier, worded the way
  // the client will read it on the bill.
  const invoiceSeed = data ? priced.lines.map(l => ({
    description: `Labour ${data.from} to ${data.to} — ${l.n} ${Number(l.n) === 1 ? 'person' : 'people'} on site`,
    item_type: 'item' as const,
    quantity: l.hours,
    unit: 'hr',
    unit_price: l.rate,
  })) : [];

  const copySummary = () => {
    if (!data) return;
    const lines = [
      `Labour ${data.from} to ${data.to}`,
      ...priced.lines.map(l => `  ${l.hours}h with ${l.n} on site @ ${money(l.rate)}/hr = ${money(l.total)}`),
      `  TOTAL ${money(priced.total)}  (${data.totals.billable_hours} billable hrs, ${data.totals.man_hours} man-hrs)`,
    ].join('\n');
    navigator.clipboard.writeText(lines).catch(() => {});
  };

  const headcounts = data ? Object.keys(data.totals.by_headcount).sort((a, b) => Number(b) - Number(a)) : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-5 py-3 text-left">
        <span className="material-symbols-outlined text-[#C77D3F]">calculate</span>
        <span className="flex-1">
          <span className="block text-sm font-bold text-gray-900">Billing tally — crew hours for invoicing</span>
          <span className="block text-xs text-gray-500">
            Wall-clock hours by how many were on site, so short-handed days bill at the right rate
          </span>
        </span>
        <span className={`material-symbols-outlined text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>expand_more</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 p-5 space-y-4">
          {/* Range + job */}
          <div className="flex items-end gap-2 flex-wrap">
            <label className="flex flex-col gap-0.5 text-xs text-gray-500">From
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm text-gray-800" />
            </label>
            <label className="flex flex-col gap-0.5 text-xs text-gray-500">To
              <input type="date" value={to} onChange={e => setTo(e.target.value)}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm text-gray-800" />
            </label>
            <label className="flex flex-col gap-0.5 text-xs text-gray-500">Job
              <select value={jobId} onChange={e => setJobId(e.target.value)}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white text-gray-800 max-w-[14rem]">
                <option value="">All jobs</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            </label>
            <button onClick={run} disabled={loading}
              className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold disabled:opacity-50">
              {loading ? 'Working…' : 'Build tally'}
            </button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {data && (
            <>
              {/* Pricing */}
              <div className="bg-[#FAF6F0] rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">What to invoice</p>
                {priced.lines.length === 0 ? (
                  <p className="text-sm text-gray-500">No hours in this range.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {priced.lines.map(l => (
                        <tr key={l.n}>
                          <td className="py-1 text-gray-700">
                            <strong>{l.hours}h</strong> with {l.n} {Number(l.n) === 1 ? 'person' : 'people'} on site
                          </td>
                          <td className="py-1 text-right whitespace-nowrap">
                            <span className="text-gray-400 text-xs mr-1">@</span>
                            <input type="number" step="1" min="0" value={l.rate}
                              onChange={e => setRate(l.n, e.target.value)}
                              className="w-20 h-8 px-2 rounded-lg border border-gray-300 text-sm text-right" />
                            <span className="text-gray-400 text-xs ml-1">/hr</span>
                          </td>
                          <td className="py-1 text-right font-bold text-gray-900 w-28">{money(l.total)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-300">
                        <td className="py-2 font-bold text-gray-900">Total labour</td>
                        <td />
                        <td className="py-2 text-right text-lg font-black text-[#C77D3F]">{money(priced.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                  <span><strong className="text-gray-700">{data.totals.billable_hours}</strong> billable hrs</span>
                  <span><strong className="text-gray-700">{data.totals.man_hours}</strong> man-hrs worked</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={copySummary} className="font-semibold text-primary">Copy for invoice</button>
                    {/* CR #174 — jump straight into a prefilled invoice */}
                    <button
                      onClick={() => selectedJob ? setInvoiceOpen(true) : undefined}
                      disabled={!selectedJob || priced.lines.length === 0}
                      title={selectedJob ? 'Open an invoice form with these lines filled in' : 'Pick a job above first — the invoice needs a client'}
                      className="h-8 px-3 rounded-lg bg-primary text-white font-bold disabled:opacity-40">
                      Create invoice
                    </button>
                  </div>
                </div>
                {!selectedJob && priced.lines.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">To invoice this, pick a job in the filter above — that tells us which client to bill.</p>
                )}
                {invoiceMsg && (
                  <p className="text-xs font-semibold text-green-700 mt-2">{invoiceMsg}</p>
                )}
              </div>

              {/* Day-by-day */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-semibold">Day</th>
                      <th className="py-2 font-semibold">On site</th>
                      {headcounts.map(n => <th key={n} className="py-2 px-2 font-semibold text-right whitespace-nowrap">{n}-up hrs</th>)}
                      <th className="py-2 pl-3 pr-4 font-semibold text-right">Billable</th>
                      <th className="py-2 pl-3 font-semibold">Short-handed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.days.map(d => (
                      <tr key={d.date} className="hover:bg-gray-50">
                        <td className="py-2 text-gray-700 whitespace-nowrap">{fmtDay(d.date)}</td>
                        <td className="py-2 text-gray-500 max-w-[180px] truncate" title={d.crew.join(', ')}>{d.crew.join(', ')}</td>
                        {headcounts.map(n => (
                          <td key={n} className="py-2 px-2 text-right text-gray-700">{d.by_headcount[n] ?? '—'}</td>
                        ))}
                        <td className="py-2 pl-3 pr-4 text-right font-semibold text-gray-900">{d.billable_hours}</td>
                        <td className="py-2 pl-3 text-xs">
                          {d.solo.length > 0
                            ? <span className="text-amber-700">{d.solo.map(s => `${s.worker} alone ${s.hours}h`).join('; ')}</span>
                            : <span className="text-gray-300">—</span>}
                          {d.untimed.length > 0 && (
                            <span className="block text-gray-400">
                              no clock times: {d.untimed.map(u => `${u.worker} ${u.hours}h`).join(', ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400">
                Entries without start/end times can't be overlapped — they're listed per day as "no clock times" and
                excluded from the wall-clock totals so nothing is over-billed.
              </p>
            </>
          )}
        </div>
      )}
      {invoiceOpen && selectedJob && (
        <InvoiceModal
          isOpen={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          onSave={saveInvoice}
          clientId={Number(selectedJob.clientId) || 0}
          clientName={(selectedJob as Job & { client?: string }).client || ''}
          jobs={[{ id: selectedJob.id, title: selectedJob.title }]}
          defaultJobId={selectedJob.id}
          seedItems={invoiceSeed}
        />
      )}
    </div>
  );
};
